import React, { useRef, useState, useEffect, useCallback } from "react";
import { Tooltip } from "antd";
import PropTypes from "prop-types";

const TRUNCATE_STYLE = {
  display: "inline-block",
  maxWidth: "90%",
  width: "90%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "middle",
};

const TOOLTIP_OVERLAY_STYLE = {
  maxHeight: "300px",
  overflowY: "auto",
  whiteSpace: "normal",
};

export function TruncatedTooltipText({ text, partial = true, underline = false }) {
  const textRef = useRef(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkOverflow();
  }, [text, checkOverflow]);

  useEffect(() => {
    if (!partial) return;
    const el = textRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [partial, text, checkOverflow]);

  const displayText = text ?? "";
  const textStyle = {
    ...TRUNCATE_STYLE,
    cursor: partial ? "pointer" : "default",
    textDecoration: underline ? "underline" : "none",
  };

  const content = (
    <div ref={textRef} style={textStyle}>
      {displayText}
    </div>
  );

  const wrapWithTooltip = (node) => (
    <Tooltip title={displayText} overlayInnerStyle={TOOLTIP_OVERLAY_STYLE}>
      {node}
    </Tooltip>
  );

  if (!partial) {
    return wrapWithTooltip(
      <div style={{ ...TRUNCATE_STYLE, width: "100%", cursor: "default" }}>{displayText}</div>
    );
  }

  return isOverflowed ? wrapWithTooltip(content) : content;
}

TruncatedTooltipText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  partial: PropTypes.bool,
  underline: PropTypes.bool,
};
